/* src/map/originRebase.ts — the floating origin (PLAN.md Part 2 §2, HANDOFF.md rule 4).
 *
 * WebGL vertex buffers are float32: a 24-bit significand, roughly 7.2 decimal digits. Web
 * Mercator metres near Sydney sit at ~1.68e7, where one float32 ULP is about 2 metres. Feed
 * those numbers to the GPU directly and street-level geometry visibly swims and z-fights as
 * the camera moves. Rebasing to an origin near the camera puts vertices in the ~1e3–1e4 range
 * instead, where a ULP is well under a millimetre.
 *
 * The invariant that makes this safe: every entity's authoritative position is lon/lat, never
 * a Three.js Vector3. On rebase, positions are re-derived from lon/lat — never incrementally
 * shifted, which would accumulate error with every rebase.
 */

import { mercToMetreScale, project } from './tileMath.ts';

export interface Origin {
  readonly lon: number;
  readonly lat: number;
  readonly mercX: number;
  readonly mercY: number;
  /** cos(lat) at the origin — converts Mercator metres to true ground metres. */
  readonly scale: number;
}

/**
 * Rebase once the camera is this far from the current origin. Chosen so worst-case vertex
 * magnitude stays around 1e4 m, three orders of magnitude inside float32's comfortable range
 * (Assumption — not an observed value).
 */
export const REBASE_THRESHOLD_M = 4000;

export function makeOrigin(lonDeg: number, latDeg: number): Origin {
  const [mercX, mercY] = project(lonDeg, latDeg);
  return { lon: lonDeg, lat: latDeg, mercX, mercY, scale: mercToMetreScale(latDeg) };
}

/**
 * lon/lat → local world metres, relative to `origin`.
 * Returns `[x, z]`: Mercator north maps to −Z, matching Three.js's default view direction.
 */
export function toLocal(lonDeg: number, latDeg: number, origin: Origin): [number, number] {
  const [mx, my] = project(lonDeg, latDeg);
  return [(mx - origin.mercX) * origin.scale, -(my - origin.mercY) * origin.scale];
}

/** Web Mercator metres → local world metres. */
export function mercToLocal(mercX: number, mercY: number, origin: Origin): [number, number] {
  return [(mercX - origin.mercX) * origin.scale, -(mercY - origin.mercY) * origin.scale];
}

/** Local world metres → Web Mercator metres. The exact inverse of `mercToLocal`. */
export function localToMerc(x: number, z: number, origin: Origin): [number, number] {
  return [x / origin.scale + origin.mercX, -z / origin.scale + origin.mercY];
}

/** Distance from the origin, in local world metres. */
export function distanceFromOrigin(lonDeg: number, latDeg: number, origin: Origin): number {
  const [x, z] = toLocal(lonDeg, latDeg, origin);
  return Math.hypot(x, z);
}

/**
 * Returns a new origin when the camera has drifted past the threshold, otherwise `null`.
 * Callers re-derive every live object's local position from its lon/lat when this returns
 * non-null.
 */
export function maybeRebase(
  cameraLon: number,
  cameraLat: number,
  origin: Origin,
): Origin | null {
  if (distanceFromOrigin(cameraLon, cameraLat, origin) < REBASE_THRESHOLD_M) return null;
  return makeOrigin(cameraLon, cameraLat);
}
